import { API_URL } from "../../utils/constants";
import { fetchWithAuth } from "../../utils/auth";
import { DocumentType, IDocument } from "../documentService";
import { ILeaseOccupant } from "../leaseService";

export interface ITenant {
  tenant_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  birth_date: string;
  gender: "male" | "female";
  nationality_code: string;
  onboarding_step: number;
  language: string;
  created_at: string;
  updated_at: string;
  documents: IDocument[];
}

export const fetchTenant = async (): Promise<ITenant> => {
  const response = await fetchWithAuth(`${API_URL}/tenants`, {
    method: "GET",
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message);

  return data;
};

export const updateTenant = async (
  email: string,
  phone_number: string,
  birth_date: string,
  nationality: string
): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/tenants`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      phone_number,
      birth_date,
      nationality,
    }),
  });

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateOnboardingStep = async (
  tenant_id: number,
  onboarding_step: number
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/${tenant_id}/onboarding`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        onboarding_step,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateOnboardingPassword = async (
  tenant_id: number,
  new_password: string
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/${tenant_id}/onboarding-password`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        new_password,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateHandoverStep = async (
  tenant_id: number,
  handover_step: number
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/${tenant_id}/handover`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        handover_step,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateTenantPassword = async (
  tenant_id: number,
  current_password: string,
  new_password: string
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/${tenant_id}/password`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        current_password,
        new_password,
      }),
    }
  );

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const updateTenantLanguage = async (
  language: string
): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/tenants/language`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ language }),
  });

  if (response.status === 200) return Promise.resolve();
  const data = await response.json();
  throw new Error(data.message);
};

export const uploadTenantAvatar = async (avatarFile: File): Promise<string> => {
  const formData = new FormData();
  formData.append("avatar", avatarFile);

  const response = await fetchWithAuth(`${API_URL}/tenants/avatar`, {
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();
  if (response.status === 200) return data.avatar;
  else throw new Error(data.message);
};

export const uploadTenantDocuments = async (
  document_type: DocumentType,
  files: File[],
  valid_until_dates: (string | null)[] = [],
  document_identifiers: (string | null)[] = [],
  credit_score: number | null = null
): Promise<IDocument[]> => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`file_${index}`, file);
    if (valid_until_dates[index]) {
      formData.append(
        `valid_until_${index}`,
        valid_until_dates[index] as string
      );
    }
    if (document_identifiers[index]) {
      formData.append(
        `document_identifier_${index}`,
        document_identifiers[index] as string
      );
    }
  });

  if (credit_score !== null) {
    formData.append("credit_score", String(credit_score));
  }

  const response = await fetchWithAuth(`${API_URL}/tenants/${document_type}`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (response.status === 201) return data.documents;
  else throw new Error(data.message);
};

export const updatePassportIdentifier = async (
  document_identifier: string | null
): Promise<IDocument> => {
  const response = await fetchWithAuth(`${API_URL}/tenants/passport`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      document_identifier,
    }),
  });

  const data = await response.json();
  if (response.status === 200) return data;
  else throw new Error(data);
};

export const createSignRequest = async (lease_id: number): Promise<string> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/create-sign-request`,
    {
      method: "POST",
    }
  );

  const data = await response.json();
  if (response.status === 201) return data.zoho_sign_request_id;
  else throw new Error(data.message);
};

export const updateTenantLeaseOccupantDetails = async (
  lease_id: number,
  occupant_id: number,
  name: string,
  email: string,
  phone_number: string | null
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/occupants/${occupant_id}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        email,
        phone_number,
      }),
    }
  );

  const data = await response.json();
  if (response.status === 200) return Promise.resolve();
  else throw new Error(data.message);
};

export const addTenantLeaseOccupant = async (
  lease_id: number,
  name: string,
  email: string,
  phone_number: string
): Promise<ILeaseOccupant> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/occupants`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        email,
        phone_number,
      }),
    }
  );

  const data = await response.json();
  if (response.status === 201) return data;
  else throw new Error(data.message);
};

export const removeTenantLeaseOccupant = async (
  lease_id: number,
  occupant_id: number
): Promise<void> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/occupants/${occupant_id}`,
    {
      method: "DELETE",
    }
  );

  const data = await response.json();
  if (response.status === 200) return Promise.resolve();
  else throw new Error(data.message);
};

export const downloadTenantLeaseDocument = async (
  lease_id: number,
  document_id: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/documents/${document_id}/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const downloadEjariDocuments = async (
  lease_id: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/ejari-documents/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const uploadTenantLeaseOccupantDocuments = async (
  lease_id: number,
  occupant_id: number,
  document_type: DocumentType,
  files: File[],
  valid_until_dates: (string | null)[] = [],
  document_identifiers: (string | null)[] = []
): Promise<IDocument[]> => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`file_${index}`, file);
    if (valid_until_dates[index]) {
      formData.append(
        `valid_until_${index}`,
        valid_until_dates[index] as string
      );
    }
    if (document_identifiers[index]) {
      formData.append(
        `document_identifier_${index}`,
        document_identifiers[index] as string
      );
    }
  });

  const response = await fetchWithAuth(
    `${API_URL}/tenants/leases/${lease_id}/occupants/${occupant_id}/documents/${document_type}`,
    {
      method: "POST",
      body: formData,
    }
  );

  const data = await response.json();
  if (response.status === 201) return data.documents;
  else throw new Error(data.message);
};
